import { type ComponentProps, type ReactNode, useEffect, useId, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Link } from "react-router-dom";
import { useAuth } from "../lib/auth/auth-context";
import { useIsAdmin } from "../lib/auth/use-is-admin";
import { isFlambeauEmail } from "../lib/calendar/email";
import { useGoogleCalendarLink } from "../lib/calendar/use-google-calendar-link";
import { Icon } from "./ui/Icon";

type IconName = ComponentProps<typeof Icon>["name"];

// Rangée standard du menu : lien avec icône + chevron (patron nav-row existant).
function MenuRow({ to, icon, label }: { to: string; icon: IconName; label: string }) {
  return (
    <Link to={to} className="nav-row rounded-md border border-line">
      <Icon name={icon} size={18} className="text-muted" />
      <span className="text-small">{label}</span>
      <Icon name="chevron-down" size={16} className="ml-auto -rotate-90 text-muted" />
    </Link>
  );
}

// Menu global de l'app (remplace HomeMenu) : burger stable en haut à droite de
// chaque écran, contenu IDENTIQUE partout — « Accueil » en tête (LE retour
// explicite à l'accueil), nav secondaire, entrées admin gatées, compte Google
// (flambeau), pages légales, déconnexion. Accessible : bouton avec
// aria-haspopup/aria-expanded, panneau role=dialog aria-modal, fermeture sur
// Échap / clic overlay / activation d'un élément, focus déplacé à l'ouverture
// et restauré à la fermeture. iOS (WKWebView) et web : pointer + clavier OK.
export function AppMenu() {
  const [open, setOpen] = useState(false);
  const titleId = useId();
  const panelRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);

  const { user, signOut } = useAuth();
  const isAdmin = useIsAdmin();
  const isFlambeau = isFlambeauEmail(user?.email);
  const calendar = useGoogleCalendarLink();

  // Échap ferme. On l'écoute au niveau document tant que le menu est ouvert.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open]);

  // Focus déplacé dans le panneau à l'ouverture, restauré sur le burger à la
  // fermeture SEULEMENT (pas au montage : le menu vit sur tous les écrans,
  // il ne doit pas voler le focus à chaque navigation).
  const wasOpenRef = useRef(false);
  useEffect(() => {
    if (open) {
      panelRef.current?.focus();
    } else if (wasOpenRef.current) {
      triggerRef.current?.focus();
    }
    wasOpenRef.current = open;
  }, [open]);

  let googleLabel: ReactNode = "Connecter compte Google";
  if (calendar.connected) {
    googleLabel = (
      <>
        Compte Google connecté
        <Icon name="check" size={14} className="text-emerald-deep" />
      </>
    );
  } else if (calendar.reconnecting) {
    googleLabel = "Reconnexion Google…";
  } else if (calendar.linked) {
    googleLabel = "Rafraîchir l'accès Google";
  }

  return (
    <>
      <button
        type="button"
        ref={triggerRef}
        className="home-burger"
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-label="Menu"
        onClick={() => setOpen(true)}
      >
        <Icon name="menu" size={22} />
      </button>

      {/* Rendu via portail sur <body> : le scrim `position: fixed` doit se repérer
          par rapport au viewport (un ancêtre transformé par l'animation d'entrée
          créerait un bloc conteneur qui rogne le panneau). */}
      {open &&
        createPortal(
          <div className="home-menu-scrim">
            {/* Overlay : clic = fermeture. Bouton dédié pour rester accessible. */}
            <button
              type="button"
              data-testid="home-menu-overlay"
              className="home-menu-overlay"
              aria-label="Fermer le menu"
              onClick={() => setOpen(false)}
            />
            <div
              ref={panelRef}
              role="dialog"
              aria-modal="true"
              aria-labelledby={titleId}
              tabIndex={-1}
              className="home-menu-panel"
            >
              <div className="home-menu-head">
                <div className="flex min-w-0 flex-col">
                  <span id={titleId} className="font-display text-body font-bold text-ink">
                    Menu
                  </span>
                  {user?.email && (
                    <span className="truncate text-label text-muted">{user.email}</span>
                  )}
                </div>
                <button
                  type="button"
                  className="home-menu-close"
                  aria-label="Fermer le menu"
                  onClick={() => setOpen(false)}
                >
                  <Icon name="x" size={18} />
                </button>
              </div>
              {/* Activer un élément (lien/bouton) ferme le menu : on capture le
                  clic dans le panneau. */}
              {/* biome-ignore lint/a11y/useKeyWithClickEvents: fermeture déléguée au clic des items ; Échap couvre le clavier */}
              {/* biome-ignore lint/a11y/noStaticElementInteractions: conteneur de fermeture, les cibles réelles restent interactives */}
              <div className="home-menu-body" onClick={() => setOpen(false)}>
                <MenuRow to="/" icon="house" label="Accueil" />
                <MenuRow to="/import" icon="file-text" label="Importer une transcription" />
                <MenuRow to="/persons" icon="users" label="Personnes et organisations" />
                {/* Corbeille des prises abandonnées (incident 2026-09-04). */}
                <MenuRow to="/corbeille" icon="trash-2" label="Corbeille" />
                {isAdmin && <MenuRow to="/depenses-ia" icon="dollar-sign" label="Dépenses IA" />}
                {isAdmin && (
                  <MenuRow to="/retroactions" icon="message-circle" label="Rétroactions" />
                )}
                {isAdmin && (
                  <MenuRow to="/accueil-testeurs" icon="user-plus" label="Accueil des testeurs" />
                )}
                {isFlambeau && (
                  <button
                    type="button"
                    onClick={calendar.connect}
                    disabled={calendar.connected || calendar.reconnecting}
                    className="nav-row rounded-md border border-line text-left disabled:opacity-60"
                  >
                    <Icon name="calendar" size={18} className="text-muted" />
                    <span className="flex items-center gap-1.5 text-small">{googleLabel}</span>
                  </button>
                )}
                {/* Pages légales — publiques, ouvertes à tous les comptes, juste
                    avant la déconnexion. */}
                <MenuRow to="/confidentialite" icon="shield" label="Vos données" />
                <MenuRow to="/conditions" icon="file-text" label="Conditions d'utilisation" />
                <button
                  type="button"
                  onClick={() => signOut()}
                  className="nav-row rounded-md border border-line text-left text-red-deep"
                >
                  <Icon name="log-out" size={18} />
                  <span className="text-small">Se déconnecter</span>
                </button>
                {calendar.error && <p className="m-0 text-small text-red-deep">{calendar.error}</p>}
              </div>
            </div>
          </div>,
          document.body,
        )}
    </>
  );
}
